import { useEffect, useMemo, useState } from "react";
import { Check, Loader2, Plus, Trash2, X } from "lucide-react";

const fieldLabels = {
  body: "Body",
  heading: "Heading",
  icon: "Icon",
  label: "Label",
  subtitle: "Subtitle",
  text: "Text",
  title: "Title",
  value: "Value",
};

function normalizeItem(item, fields) {
  if (typeof item === "string") {
    return { value: item };
  }

  return fields.reduce((next, field) => {
    next[field] = item?.[field] ?? "";
    return next;
  }, {});
}

export default function CmsEditableList({
  editMode = false,
  fieldKey,
  fields = ["title", "text"],
  itemName = "item",
  label,
  onSave,
  sectionKey,
  value = [],
}) {
  const normalized = useMemo(
    () => (Array.isArray(value) ? value : []).map((item) => normalizeItem(item, fields)),
    [fields, value]
  );
  const [draftItems, setDraftItems] = useState(normalized);
  const [saving, setSaving] = useState(false);
  const dirty = JSON.stringify(draftItems) !== JSON.stringify(normalized);

  useEffect(() => {
    setDraftItems(normalized);
  }, [normalized]);

  if (!editMode) return null;

  const updateItem = (index, field, nextValue) => {
    setDraftItems((current) => current.map((item, itemIndex) =>
      itemIndex === index ? { ...item, [field]: nextValue } : item
    ));
  };

  const addItem = () => {
    const empty = fields.reduce((item, field) => ({ ...item, [field]: "" }), {});
    setDraftItems((current) => [...current, empty]);
  };

  const removeItem = (index) => {
    setDraftItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = fields.length === 1 && fields[0] === "value" ? draftItems.map((item) => item.value) : draftItems;
      await onSave?.(sectionKey, fieldKey, payload);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mb-4 rounded-2xl border border-dashed border-[#4391d1]/50 bg-white p-4 shadow-sm ring-4 ring-blue-50/60">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="m-0 text-xs font-black uppercase tracking-widest text-[#1278b5]">
            Editable {label || `${sectionKey}.${fieldKey}`}
          </p>
          <p className="m-0 mt-1 text-xs font-semibold text-gray-500">
            Edit these {itemName}s as structured fields.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <button
            className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-black uppercase text-gray-700 hover:border-[#4391d1] hover:text-[#1278b5]"
            disabled={saving}
            onClick={addItem}
            type="button"
          >
            <Plus className="h-3.5 w-3.5" />
            Add
          </button>
          {dirty && (
            <>
              <button
                className="inline-flex h-9 items-center gap-2 rounded-lg bg-[#4391d1] px-3 text-xs font-black uppercase text-white hover:bg-blue-600"
                disabled={saving}
                onClick={save}
                type="button"
              >
                {saving ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Check className="h-3.5 w-3.5" />}
                Save
              </button>
              <button
                className="inline-flex h-9 items-center gap-2 rounded-lg border border-gray-200 bg-white px-3 text-xs font-black uppercase text-gray-600 hover:border-gray-300"
                disabled={saving}
                onClick={() => setDraftItems(normalized)}
                type="button"
              >
                <X className="h-3.5 w-3.5" />
                Cancel
              </button>
            </>
          )}
        </div>
      </div>

      <div className="space-y-3">
        {draftItems.map((item, index) => (
          <div key={index} className="rounded-xl border border-gray-100 bg-gray-50 p-3">
            <div className="mb-3 flex items-center justify-between gap-3">
              <span className="text-xs font-black uppercase text-gray-500">
                {itemName} {index + 1}
              </span>
              <button
                className="inline-flex h-8 items-center gap-1 rounded-lg border border-red-100 bg-white px-2 text-xs font-black uppercase text-red-600 hover:bg-red-50"
                disabled={saving}
                onClick={() => removeItem(index)}
                type="button"
              >
                <Trash2 className="h-3.5 w-3.5" />
                Remove
              </button>
            </div>
            <div className="grid gap-3 md:grid-cols-2">
              {fields.map((field) => {
                const multiline = ["body", "text"].includes(field);
                const Input = multiline ? "textarea" : "input";

                return (
                  <label key={field} className={multiline ? "md:col-span-2" : ""}>
                    <span className="mb-1 block text-[10px] font-black uppercase tracking-widest text-gray-500">
                      {fieldLabels[field] || field}
                    </span>
                    <Input
                      className="w-full resize-y rounded-lg border border-gray-200 bg-white px-3 py-2 text-sm font-semibold text-slate-800 outline-none focus:border-[#4391d1] focus:ring-2 focus:ring-blue-100"
                      disabled={saving}
                      onChange={(event) => updateItem(index, field, event.target.value)}
                      rows={multiline ? 3 : undefined}
                      value={item[field] || ""}
                    />
                  </label>
                );
              })}
            </div>
          </div>
        ))}
      </div>

      {saving && (
        <p className="m-0 mt-3 inline-flex items-center gap-2 text-xs font-black uppercase text-[#1278b5]">
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
          Saving changes
        </p>
      )}
    </div>
  );
}
